/* «Награды» в серверном режиме: задания, серия и партнёрские числа с сервера ложатся в состояние шаблона (S.srv, задания с sv) — разметка и движение те же.
   Начисление идёт через намерение в sessionStorage: пока исход запроса не подтверждён, новые начисления заблокированы до проверки прежнего. */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { PartnerOverview } from '../../api/partner';
import type { RewardsResponse, RewardTask, StoryMaterial } from '../../api/rewards';
import type { Me } from '../../api/types';
import { applyPartnerRef, partnerLevels } from '../../bridge/partner-ref';
import { buildTasks, type ServerTaskState, type ServerView, type Task, type TaskState } from './data';
import { clearRewardIntent, definitiveRewardError, readRewardIntent, saveRewardIntent, type RewardIntent } from './reward-intent';
import { getRt } from './runtime';
import { uiText } from '../../i18n';

/** Задание шаблона по метрике сервера: от него берутся значок и подпись */
const BASE: Record<string, string> = {
  works: 'works',
  clones: 'clone',
  photos: 'photo',
  reels: 'reels',
  covers: 'cover',
  avatars: 'avatar',
  story_shared: 'story',
  identity_linked: 'sid',
  tts: 'dtts',
  prompts: 'prompt',
  shares: 'share',
  tools: 'wtools',
  chat: 'chat',
  friends: 'f1',
  buyers: 'fbuy',
};
/** Экран задания сервера → переход шаблона: разделы через ui.go, экраны студии через ui.openApp */
const SECTIONS = new Set(['studio', 'clone', 'tts', 'history', 'profile', 'partner']);
function routeOf(x: RewardTask): ServerTaskState['route'] {
  const s = x.action?.kind === 'navigate' ? x.action.screen : null;
  if (!s) return null;
  if (SECTIONS.has(s)) return { go: s };
  if (s === 'circle') return { app: { src: 'char' } };
  return ['photo', 'reels', 'cover', 'avatar', 'text'].includes(s) ? { app: { screen: s } } : null;
}
const SCREEN_IC: Record<string, string> = {
  studio: 'sparkle',
  circle: 'send',
  clone: 'mic',
  photo: 'image',
  reels: 'film',
  cover: 'music',
  avatar: 'face',
  tts: 'wave',
  history: 'share',
  text: 'wand',
  partner: 'users',
};

export const emptyView = (): ServerView => ({
  ready: false,
  err: '',
  busy: false,
  pending: false,
  blocked: false,
  days: [],
  dayAmt: 0,
  nextAmt: 0,
  streakN: 0,
  dayAt: null,
  weekAt: null,
  pt: null,
});

const SR = {
  owner: null as number | null,
  me: null as Me | null,
  data: null as RewardsResponse | null,
  intent: null as RewardIntent | null,
  ctl: null as AbortController | null,
  running: false,
  base: null as Task[] | null,
};

const ms = (v?: string | null) => (v && Number.isFinite(Date.parse(v)) ? Date.parse(v) : null);
const patch = (o: Partial<ServerView>) => getRt().store.update(s => void (s.srv = { ...(s.srv ?? emptyView()), ...o }));

function stateOf(x: RewardTask): TaskState {
  if (x.state === 'unavailable' || x.available === false || x.progress_available === false || x.progress === null) return 'todo';
  if (x.state === 'claim') return 'claim';
  return x.state === 'done' || x.state === 'claimed' ? 'done' : 'todo';
}

function toTask(x: RewardTask, base: Task[]): Task {
  /* кружки по вкладке: ежедневный «Персонаж или вы сами», недельный, разовый «Первый кружок» */
  const key = x.metric === 'circles' ? (x.tab === 'week' ? 'wcircles' : x.tab === 'day' ? 'dcircle' : 'circle') : BASE[x.metric ?? ''];
  const b = base.find(t => t.id === key);
  const kind = x.action?.kind;
  const off =
    x.state === 'unavailable' || x.available === false
      ? 'Задание пока недоступно'
      : x.progress === null || x.progress_available === false
        ? 'Прогресс временно недоступен'
        : '';
  return {
    id: x.id,
    tab: x.tab,
    ic: b?.ic ?? SCREEN_IC[x.action?.screen ?? ''] ?? 'gift',
    svg: b?.svg,
    t: x.title,
    s: off || b?.s,
    r: x.reward,
    /* ступени лестницы друзей: задания вкладки «Друзья» по числу приглашённых */
    src: x.tab === 'fr' && x.metric !== 'buyers' ? 'act' : undefined,
    n: x.target > 1 || x.tab === 'fr' ? x.target : undefined,
    go: kind === 'story' ? 'story' : kind === 'identity' ? 'sid' : kind === 'partner' ? 'ref' : kind === 'chat' ? 'chat' : 'nav',
    sv: { st: stateOf(x), pv: x.progress ?? 0, off, route: routeOf(x), tpl: x.action?.template_id },
  };
}

function partnerView(p: PartnerOverview, friends?: RewardsResponse['friends']): ServerView['pt'] {
  return {
    act: friends?.active ?? 0,
    buy: friends?.buyers ?? p.buyers_all,
    earn: p.earned,
    tiers: partnerLevels(p).map(l => ({ name: l.name, pct: l.pct, from: l.buyers })),
  };
}

/** Снимок сервера в состояние шаблона: задания, день серии, сброс, партнёрские числа (undefined — прежние) */
function apply(d: RewardsResponse, p: PartnerOverview | null | undefined) {
  if (!Array.isArray(d.streak.days) || !d.streak.days.length || d.streak.days.some(amount => !Number.isFinite(amount) || amount < 0))
    throw new Error('reward_streak_unavailable');
  const r = getRt();
  SR.base ??= buildTasks(r.BC);
  const base = SR.base;
  r.TASKS.splice(0, r.TASKS.length, ...d.tasks.map(x => toTask(x, base)));
  const k = d.streak,
    len = k.days.length,
    day = Math.max(1, Math.min(len, k.day));
  r.store.update(s => {
    s.today = k.today_claimed;
    s.streak = k.today_claimed ? day : day - 1;
    const prev = s.srv ?? emptyView();
    s.srv = {
      ...prev,
      ready: true,
      days: k.days,
      dayAmt: k.current_amount ?? k.days[day - 1],
      nextAmt: k.next_amount ?? k.days[day % len],
      streakN: k.consecutive ?? s.streak,
      friendsKnown: !!d.friends,
      dayAt: ms(d.day_resets_at ?? k.resets_at),
      weekAt: ms(d.week_resets_at),
      pt:
        p === undefined
          ? prev.pt
          : p
            ? partnerView(p, d.friends)
            : d.friends
              ? { act: d.friends.active, buy: d.friends.buyers, earn: 0, tiers: [] }
              : null,
    };
  });
}

function publishMe(me: Me) {
  window.BotServerMe = me;
  document.dispatchEvent(new CustomEvent('steos:account-updated', { detail: me }));
}

/** Награды, профиль и партнёрские числа одним снимком; смена аккаунта между загрузками — снимок не принимается */
export async function srvLoad() {
  SR.ctl?.abort();
  const ctl = new AbortController();
  SR.ctl = ctl;
  try {
    const [d, me, p] = await Promise.all([
      endpoints.rewards.get(ctl.signal),
      endpoints.me(),
      endpoints.partner.overview('week', ctl.signal).catch(() => null),
    ]);
    if (ctl.signal.aborted) return;
    if (SR.owner !== null && SR.owner !== me.user.id) throw new Error('reward_owner_changed');
    SR.owner = me.user.id;
    SR.me = me;
    SR.data = d;
    if (p) applyPartnerRef(p);
    let blocked = false;
    try {
      SR.intent = readRewardIntent(me.user.id);
    } catch {
      SR.intent = null;
      blocked = true;
    }
    apply(d, p);
    patch({
      pending: !!SR.intent,
      blocked,
      err: blocked ? 'Не удалось восстановить прежний запрос награды. Проверьте баланс и напишите в поддержку' : '',
    });
    publishMe(me);
  } catch {
    if (!ctl.signal.aborted) patch({ err: 'Награды временно недоступны. Попробуйте обновить данные' });
  }
}

function failText(cause: unknown, intent: RewardIntent, accepted: boolean) {
  if (accepted) return 'Награда подтверждена. Обновите данные: повторять начисление не нужно';
  const code = !intent.unknown && cause instanceof ApiError ? cause.code : '';
  if (code === 'reward_not_ready') return 'Задание ещё не выполнено. Обновите прогресс';
  if (code === 'reward_claimed') return 'Награда уже получена. Обновите данные';
  return 'Начисление не подтверждено. Проверьте прежний запрос: новая награда пока заблокирована';
}

/** Начисление по намерению: намерение сохраняется до запроса, снимается только по ответу сервера или окончательной ошибке */
async function execute(intent: RewardIntent, el?: HTMLElement | null) {
  const r = getRt();
  if (!SR.me || intent.owner !== SR.me.user.id || SR.running) return;
  SR.running = true;
  patch({ busy: true, err: '' });
  let persisted = false,
    accepted = false;
  try {
    const durable = { ...intent, unknown: true };
    saveRewardIntent(durable);
    persisted = true;
    SR.intent = durable;
    patch({ pending: true });
    if (intent.kind === 'story') await endpoints.rewards.shared(intent.taskId!, intent.templateId!, intent.shareProof!, intent.key);
    else await endpoints.rewards.claim(intent.kind === 'task' ? intent.taskId! : null, intent.key);
    accepted = true;
    clearRewardIntent(intent.owner);
    SR.intent = null;
    if (intent.taskId) r.popAfter.set(intent.taskId, intent.kind === 'story' ? '.rw-take' : '.st-pr');
    patch({ pending: false });
    r.ui.buzz();
    await srvLoad();
  } catch (cause) {
    if (!intent.unknown && cause instanceof ApiError && definitiveRewardError(cause)) {
      try {
        clearRewardIntent(intent.owner);
        SR.intent = null;
        patch({ pending: false });
      } catch {
        patch({ blocked: true });
      }
    }
    if (!persisted) patch({ blocked: true });
    const text = failText(cause, intent, accepted);
    patch({ err: text });
    if (el?.isConnected) r.ui.tip(el, uiText(text));
  } finally {
    SR.running = false;
    patch({ busy: false });
  }
}

/** Можно ли начислять сейчас; нельзя из-за прежнего запроса — подсказка у кнопки */
function canClaim(el?: HTMLElement | null) {
  const v = getRt().store.S.srv;
  if (!SR.data || !SR.me || !v?.ready || v.busy || SR.running) return false;
  if (v.err && !v.pending && !v.blocked) {
    if (el) getRt().ui.tip(el, uiText('Сначала обновите данные наград'));
    return false;
  }
  if (v.pending || v.blocked) {
    if (el) getRt().ui.tip(el, uiText('Сначала проверьте прежний запрос награды'));
    return false;
  }
  return true;
}

/** Забрать день серии (taskId = null) или награду задания; кнопка занята, пока идёт запрос */
export function srvClaim(taskId: string | null, el?: HTMLElement | null) {
  const d = SR.data;
  if (!canClaim(el) || !d || !SR.me) return;
  if (
    taskId
      ? !d.tasks.some(
          t => t.id === taskId && t.state === 'claim' && t.available !== false && t.progress_available !== false && t.progress !== null,
        )
      : d.streak.today_claimed
  )
    return;
  const intent: RewardIntent = {
    owner: SR.me.user.id,
    key: uuid(),
    kind: taskId ? 'task' : 'streak',
    taskId: taskId ?? undefined,
    period: taskId ? d.tasks.find(t => t.id === taskId)?.period_key : d.streak.resets_at,
  };
  const run = () => execute(intent, el);
  return el ? window.SteosUI.busy(el, run) : run();
}

/** Сторис отправлена: задание засчитывается по share_proof материала сервера */
function srvShared(taskId: string, templateId: string, material: StoryMaterial) {
  const d = SR.data;
  if (!canClaim() || !d || !SR.me || !d.tasks.some(t => t.id === taskId && t.state === 'todo')) return;
  return execute({ owner: SR.me.user.id, key: uuid(), kind: 'story', taskId, templateId, shareProof: material.share_proof });
}

/** Лист сторис ядра; материал готовит сервер (BotCore.story.serve) */
export function srvStory(t: Task) {
  getRt().BC.story.open({
    tpl: t.sv?.tpl,
    onShared: (tpl, material) => {
      if (tpl && material) void srvShared(t.id, tpl, material as StoryMaterial);
    },
  });
}

/** Проверить прежний запрос: повтор с тем же ключом идемпотентности */
export function srvRetry(el?: HTMLElement | null) {
  if (!SR.intent || SR.running) return;
  const intent = SR.intent;
  const run = () => execute(intent, el);
  return el ? window.SteosUI.busy(el, run) : run();
}

/** Пока раздел на экране: снимок раз в минуту и при возвращении в приложение (не во время начисления) */
let timer = 0;
const onVisible = () => {
  if (document.visibilityState === 'visible' && !SR.running) void srvLoad();
};
export function srvWatch(on: boolean) {
  clearInterval(timer);
  document.removeEventListener('visibilitychange', onVisible);
  if (!on) {
    SR.ctl?.abort();
    return;
  }
  timer = window.setInterval(onVisible, 60_000);
  document.addEventListener('visibilitychange', onVisible);
}
